import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { getMaxGuestCount } from 'lib/offer/occupancyUtils'

import React, { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import OfferCapacityWithAgesVerticalSelection from './OfferCapacityWithAgesVerticalSelection'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { generateOfferPageRoom, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { formToObject } from 'lib/forms/formToObject'
import noop from 'lib/function/noop'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

interface Props {
  offer: App.Offer
  onClose?: () => void
  onApply?: () => void
  footerText: string
  disableMultiRoom?: boolean
}

function OfferCapacityWithAgesVerticalSidebarSelection(props: Props) {
  const { offer, onClose = noop, onApply = noop, footerText, disableMultiRoom } = props

  const intl = useIntl()
  const modalContext = useModalElementContext()
  const dispatch = useOfferPageDispatch()
  const formRef = useRef<HTMLFormElement>(null)
  const capacities = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate?.capacities ?? [])
  }, [offer])

  const maxGuests = useMemo((): number => getMaxGuestCount(capacities), [capacities])

  const maxOccupancySubtitle = maxGuests > 0 ? intl.formatMessage(
    {
      id: 'x652o2VJ-izWcrkQivOg',
      defaultMessage: 'Maximum occupancy is {maxGuests, plural, one {# guest} other {# guests}}',
      description: 'Subtitle showing maximum guest occupancy for room selection',
    },
    { maxGuests },
  ) : undefined

  const saveRooms = useCallback(() => {
    if (formRef.current?.checkValidity()) {
      const values = formToObject<{ occupants: App.RoomOccupants }>(formRef.current)
      dispatch({
        type: OfferPageActions.setRooms,
        rooms: [generateOfferPageRoom(values.occupants)],
      })
    }
  }, [dispatch])

  const onCloseClick = useCallback(() => {
    saveRooms()
    onClose()
    modalContext?.resolve()
  }, [modalContext, onClose, saveRooms])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    saveRooms()
    onApply()
    modalContext?.resolve()
  }

  return <form onSubmit={onSubmit} ref={formRef}>
    <ModalHeader
      title={intl.formatMessage({
        id: 'SSPQn7HJCj1koltC-qSL',
        defaultMessage: 'Who is going?',
        description: 'Heading title for guest selection modal on the offer page',
      })}
      subtitle={maxOccupancySubtitle}
      onCloseButtonClick={onCloseClick}
    />
    <ModalBody>
      <ModalContent>
        <OfferCapacityWithAgesVerticalSelection
          capacities={capacities}
          disableMultiRoom={disableMultiRoom}
        />
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        children: footerText,
        type: 'submit',
      }}
    />
  </form>
}

export default withOfferPageState((state) => ({
  offer: state.offer,
}), OfferCapacityWithAgesVerticalSidebarSelection)
